import {
  SHBox,
  SHStack,
  SHStackProps,
  SHTypography,
} from "@components/design-systems";
import { ArrowDownSVG } from "@components/svgs";
import { ModelScreenerColumnSettingData } from "@models/model-screener/entities/modelScreenerFilter";
import { MSFilterDefaultValueConstants } from "@pages/managed-accounts/model-screener/components/model-screener-table/toolbar/filter/constants";
import { Collapse, IconButton, useTheme } from "@mui/material";
import { useToggle } from "react-use";
import { HamburgerSVG } from "@components/svgs/hamburger";
import { useSortable } from "@dnd-kit/sortable";
import React from "react";
import { MSTableColumnSettingIconButton } from "../tooltip-button";
import { MSTableColumnSettingSubHeaderSection } from "@pages/managed-accounts/model-screener/components/model-screener-table/toolbar/column-setting/sortable-context/sub-header-section";
import { MSTableColumnSettingMainHeaderVisibilityCheckbox } from "@pages/managed-accounts/model-screener/components/model-screener-table/toolbar/column-setting/sortable-context/main-header/visibility-checkbox";

export type MSTableColumnSettingMainHeaderProps = {
  item: ModelScreenerColumnSettingData;
  disabled?: boolean;
} & SHStackProps;

export const MSTableColumnSettingMainHeader = React.memo(
  ({ item, disabled, ...others }: MSTableColumnSettingMainHeaderProps) => {
    const { palette } = useTheme();
    const subHeaders = item.columnSettings || [];
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: item.id });
    const visibleSubHeaderCount = subHeaders.filter(s => s.isVisible).length;
    const totalSubHeaderCount = subHeaders.length;

    // States
    const [isCollapsed, toggleCollapsed] = useToggle(true);

    return (
      <SHStack
        ref={setNodeRef}
        {...others}
        {...attributes}
        {...(disabled ? undefined : listeners)}
        direction={"column"}
        position={"relative"}
        sx={{
          transform: transform
            ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
            : undefined,
          transition,
          zIndex: 1,
          cursor: "grab",
          ...isDragging && {
            cursor: "grabbing",
            zIndex: 2,
            "& .MSTableColumnSettingSubHeader": {
              borderBottom: `1px solid ${palette.primary.main}`,
              backgroundColor: palette.common.lightSteelBlue,
              filter: "drop-shadow(1px 1px 2px rgba(65, 73, 89, 0.25))",
              transition:
                "background-color 300ms ease, border-bottom 300ms ease",
            },
          },
        }}
      >
        <SHStack
          direction={"row"}
          bgcolor={palette.common.whitesmoke}
          paddingY={1}
          paddingLeft={2}
          borderRadius={"4px"}
          justifyContent={"space-between"}
          borderBottom={"1px solid transparent"}
          sx={{
            ...isDragging ? {
              borderBottom: `1px solid ${palette.primary.main}`,
              backgroundColor: palette.common.lightSteelBlue,
              filter: "drop-shadow(1px 1px 2px rgba(65, 73, 89, 0.25))",
              transition:
                "background-color 300ms ease, border-bottom 300ms ease",
            } : {},
          }}
        >
          <SHStack direction={"row"} alignItems={"center"}>
            <MSTableColumnSettingIconButton
              title={isCollapsed ? "Expand" : "Collapse"}
              icon={
                <IconButton
                  disabled={disabled}
                  onClick={toggleCollapsed}
                  onPointerDown={(e) => e.stopPropagation()}
                  sx={{
                    transform: isCollapsed ? "rotate(-90deg)" : "rotate(0deg)",
                    transition: "transform 0.3s ease",
                    ":hover": {
                      cursor: "pointer",
                    },
                    padding: 1,
                  }}
                >
                  <ArrowDownSVG
                    width={"12px"}
                    height={"12px"}
                    color={disabled ? palette.action.disabled : undefined}
                  />
                </IconButton>
              }
            />

            <SHBox
              component={"span"}
              onPointerDown={(e) => e.stopPropagation()}
            >
              <MSTableColumnSettingMainHeaderVisibilityCheckbox
                mainHeaderId={item.id}
                defaultChecked={item.isVisible}
                disabled={disabled}
              />
            </SHBox>

            <SHTypography
              variant={"subtitle2"}
              paddingLeft={1}
              disabled={disabled}
            >
              {item.name} ({visibleSubHeaderCount} of {totalSubHeaderCount} selected)
            </SHTypography>
          </SHStack>

          <SHStack marginRight={0.5}>
            <MSTableColumnSettingIconButton
              title={"Move"}
              icon={
                <IconButton disabled sx={{ padding: 1 }}>
                  <HamburgerSVG
                    width={18}
                    height={18}
                    color={disabled ? palette.action.disabled : undefined}
                  />
                </IconButton>
              }
              popperSx={{
                marginTop: "0px !important",
              }}
            />
          </SHStack>
        </SHStack>

        <Collapse
          sx={{
            width: "100%",
            overflow: "scroll",
            scrollbarWidth: "none",
            position: isDragging ? "absolute" : "static",
            zIndex: isDragging ? 999 : "auto",
            top: isDragging ? 52 : 0,
          }}
          in={!isCollapsed}
          unmountOnExit={false}
          timeout={MSFilterDefaultValueConstants.StandardDelayTimeMs}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <SHStack gap={1} paddingLeft={8} overflow={"hidden"} marginTop={1}>
            <MSTableColumnSettingSubHeaderSection
              mainHeaderId={item.id}
              subHeaders={subHeaders}
              disabled={disabled}
            />
          </SHStack>
        </Collapse>
      </SHStack>
    );
  },
);
